import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Paper,
} from '@mui/material';
import { FaWhatsapp } from 'react-icons/fa';
import { useAppSelector } from '../../../../../store';
import { useLeadStages } from '../../../hooks/useLeadStages';
import type { WhatsAppTemplate } from '../../../types/whatsappTemplate';

interface WhatsAppMessageModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  phone: string;
  whatsapp?: string;
  contactName?: string;
}

const WhatsAppMessageModal: React.FC<WhatsAppMessageModalProps> = ({
  isOpen,
  onRequestClose,
  phone,
  whatsapp,
  contactName,
}) => {
  const { templates } = useAppSelector((state) => state.whatsappTemplates);
  const { stepperStages } = useLeadStages();
  const [messageType, setMessageType] = useState<'template' | 'custom'>('template');
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [customMessage, setCustomMessage] = useState('');

  const handleSend = () => {
    const targetNumber = whatsapp || phone;
    if (!targetNumber) {
      alert('Phone number is required');
      return;
    }

    const cleaned = targetNumber.replace(/\D/g, '');
    if (!cleaned) {
      alert('Invalid phone number');
      return;
    }

    let messageToSend = '';
    if (messageType === 'template' && selectedTemplate) {
      messageToSend = selectedTemplate.message;
    } else if (messageType === 'custom') {
      messageToSend = customMessage.trim();
      if (!messageToSend) {
        alert('Please enter a custom message');
        return;
      }
    } else {
      alert('Please select a template or enter a custom message');
      return;
    }

    // Encode message for URL
    const encodedMessage = encodeURIComponent(messageToSend);
    const whatsappUrl = `https://wa.me/${cleaned}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');

    // Reset and close
    setMessageType('template');
    setSelectedTemplate(null);
    setCustomMessage('');
    onRequestClose();
  };

  const handleClose = () => {
    setMessageType('template');
    setSelectedTemplate(null);
    setCustomMessage('');
    onRequestClose();
  };

  const templatesByStage = stepperStages.map((stage) => ({
    stage: stage.key,
    label: stage.label,
    templates: templates.filter((t) => t.stage === stage.key),
  }));

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 1,
        }}
      >
        <FaWhatsapp style={{ color: '#25D366', fontSize: 24 }} />
        <Box>
          <Typography variant="h6" component="span">
            Send WhatsApp Message
          </Typography>
          {contactName && (
            <Typography variant="body2" color="text.secondary">
              to {contactName}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
          <FormLabel component="legend">Select Message Type</FormLabel>
          <RadioGroup
            row
            value={messageType}
            onChange={(e) => {
              setMessageType(e.target.value as 'template' | 'custom');
              if (e.target.value === 'custom') {
                setSelectedTemplate(null);
              }
            }}
          >
            <FormControlLabel value="template" control={<Radio />} label="Predefined Template" />
            <FormControlLabel value="custom" control={<Radio />} label="Custom Message" />
          </RadioGroup>
        </FormControl>

        {messageType === 'template' ? (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Select a template:
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                maxHeight: 300,
                overflow: 'auto',
                mt: 1,
              }}
            >
              <List>
                {templatesByStage.map((stageGroup, index) => (
                  <React.Fragment key={stageGroup.stage}>
                    {index > 0 && <Divider />}
                    {stageGroup.templates.length > 0 && (
                      <>
                        <ListItem
                          sx={{
                            bgcolor: 'grey.100',
                            py: 0.5,
                            px: 2,
                          }}
                        >
                          <Typography variant="caption" fontWeight={600}>
                            {stageGroup.label}
                          </Typography>
                        </ListItem>
                        {stageGroup.templates.map((template) => (
                          <ListItemButton
                            key={template.id}
                            selected={selectedTemplate?.id === template.id}
                            onClick={() => setSelectedTemplate(template)}
                          >
                            <ListItemText
                              primary={template.title}
                              secondary={
                                <Typography
                                  variant="caption"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block',
                                  }}
                                >
                                  {template.message}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        ))}
                      </>
                    )}
                  </React.Fragment>
                ))}
              </List>
            </Paper>

            {selectedTemplate && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Preview:
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedTemplate.message}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box>
            <TextField
              label="Custom Message"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              fullWidth
              multiline
              rows={6}
              placeholder="Type your custom WhatsApp message here..."
              helperText="This message will be pre-filled in WhatsApp"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          startIcon={<FaWhatsapp />}
          disabled={
            messageType === 'template' ? !selectedTemplate : !customMessage.trim()
          }
        >
          Open WhatsApp
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WhatsAppMessageModal;




